import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  IconButton,
  Button,
  //Tooltip,
  // FormControlLabel,
  // Switch,
} from '@mui/material';
import {
  SmartToyOutlined as SmartToyOutlinedIcon,
  TuneSharp as TuneSharpIcon,
  CheckCircle as CheckCircleIcon,
  EditOutlined as EditOutlinedIcon,
  //Block
} from '@mui/icons-material';
import RootCauseAnalysis from './RootCauseAnalysis.js'; 
import { fetchRiskCardData } from './redux/actions/riskActions.js';
import { useMergedRiskFormData } from './redux/reduxutils/riskFormUtils';

const RiskCard = ({ modelId, assessmentUuid, sectionName, sectionIsNA = false ,sectionLabel }) => {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const riskFormData = useMergedRiskFormData(modelId);

const evalTemplate =
useSelector(s => s.config.configs['riskCauseEvaluation']?.defaultCriteria) || {};
  const [expandedRows, setExpandedRows] = useState({});
  // Legacy skip logic removed - now handled by RiskAreaIntro component
  // const handleSectionNASwitch = () =>
  //   dispatch(toggleSectionNA(modelId, sectionName));

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);

  useEffect(() => {
    if (modelId && assessmentUuid) {
      setIsLoading(true);
      dispatch(fetchRiskCardData(modelId, assessmentUuid))
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch data:', error);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [dispatch, modelId, assessmentUuid]);

  const toTitleCase = (str) => {
    str = str.replace(/\b(EU|ROC|AUC)\b/g, (match) => match.toUpperCase());
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/\b([Aa]nd)\b/g, '&')
      .split(/\s+/)
      .map((word) => {
        if (['EU', 'ROC', 'AUC'].includes(word)) {
          return word;
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      })
      .join(' ')
      .trim();
  };

  const renderTableHead = () => (
    <>
      <TableRow>
        <TableCell
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151',
            borderBottom: '1px solid #E5E7EB',
            py: 2,
          }}
        >
          Risk Source
        </TableCell>
        <TableCell
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151',
            maxWidth: '250px',
            borderBottom: '1px solid #E5E7EB',
            py: 2,
          }}
        >
          Description
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151',
            borderBottom: '1px solid #E5E7EB',
            py: 2,
          }}
        >
          Analyze
        </TableCell>
      </TableRow>
    </>
  );

  const renderTableBody = () => {
    const riskSources = riskFormData[sectionName]?.riskSources || [];
    
    if (riskSources.length === 0) {
      return (
        <TableRow>
          <TableCell 
            colSpan={3} 
            sx={{ 
              textAlign: 'center', 
              py: 6,
              color: '#6B7280',
              fontSize: '0.875rem',
              fontStyle: 'italic'
            }}
          >
            No risk sources available.
          </TableCell>
        </TableRow>
      );
    }

    return riskSources.map((riskSource, idx) => {
      const completed = isRiskCompleted(riskSource);

      console.log('[RiskCard] row render', {
        source: riskSource.name,
        completed,
        value: riskSource.value,
        causes: riskSource.causes,
      });
      const isExpanded = expandedRows[riskSource.name] || false;
      
      return (
        <TableRow
          key={riskSource.name || idx}
          hover
          sx={{
            ...(sectionIsNA
              ? { opacity: 0.6, pointerEvents: 'none' }
              : {}),
            '&:last-child td': { border: 0 },
            '&:hover': {
              backgroundColor: '#F9FAFB',
            }
          }}
        >
          {/* Risk Source Name */}
          <TableCell 
            sx={{ 
              borderBottom: '1px solid #F3F4F6',
              py: 2.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#111827',
                lineHeight: 1.5,
              }}
            >
              {toTitleCase(riskSource.name)}
            </Typography>
          </TableCell>

          {/* Description */}
          <TableCell 
            sx={{ 
              borderBottom: '1px solid #F3F4F6',
              py: 2.5,
              maxWidth: '300px',
            }}
          >
            {riskSource.description ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    lineHeight: 1.5,
                    display: 'inline',
                  }}
                >
                  {isExpanded || riskSource.description.length <= 100
                    ? riskSource.description
                    : `${riskSource.description.substring(0, 100)}...`}
                </Typography>
                {riskSource.description.length > 100 && (
                  <Button
                    size="small"
                    onClick={() =>
                      setExpandedRows({
                        ...expandedRows,
                        [riskSource.name]: !isExpanded,
                      })
                    }
                    sx={{
                      color: '#3B82F6',
                      textTransform: 'none',
                      ml: 0.5,
                      p: 0,
                      minWidth: 'auto',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#1D4ED8',
                      }
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </Button>
                )}
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.875rem',
                  color: '#9CA3AF',
                  fontStyle: 'italic',
                }}
              >
                No description provided.
              </Typography>
            )}
          </TableCell>

          {/* Action */}
          <TableCell align="center" sx={{ borderBottom: '1px solid #F3F4F6', py: 2.5 }}>
  {sectionIsNA ? null : (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        '&:hover .edit': { opacity: 1 },
        '&:hover .check': { opacity: 0 },
      }}
    >
      <CheckCircleIcon
        className="check"
        sx={{ color: '#10B981', fontSize: 20, transition: 'opacity 0.2s' }}
        style={{ opacity: completed ? 1 : 0 }}
      />
      <IconButton
        className="edit"
        onClick={() => {
          setSelectedRisk({ section: sectionName, riskSource });
          setDrawerOpen(true);
        }}
        aria-label={completed ? 'Edit Risk' : 'Open Root Cause Analysis'}
        sx={{
          position: 'absolute',
          top: 0, left: 0,
          color: completed ? '#6B7280' : '#3B82F6',
          opacity: completed ? 0 : 1,
          transition: 'opacity 0.2s',
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        {completed ? <EditOutlinedIcon /> : <TuneSharpIcon />}
      </IconButton>
    </Box>
  )}
</TableCell>
        </TableRow>
      );
    });
  };

  

  const isRiskCompleted = (riskSource) => {
    const answersByValue = riskSource.causesByValue || {};
      const critKeys = Object.keys(evalTemplate)
        .filter(k => k.trim().toLowerCase() !== 'other');
      if (!critKeys.length) return Object.keys(answersByValue).length > 0;
    
      // row is complete if *any single source-value*
      // has at least one cause with all critKeys rated
      return Object.values(answersByValue).some(causeBlob =>
        Object.values(causeBlob).some(cause =>
          critKeys.every(crit => {
            const v = cause.evaluation?.[crit]?.value;
            return Array.isArray(v) ? v.length : Boolean(v);
          })
        )
      );
    };
  

  return (
    <Box sx={{ p: 3 }}>
      {/* Enhanced Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          pb: 1,
          //borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: '#374151', // Slightly lighter than main titles
              fontSize: '1rem', // 16px - more appropriate for section headers
              letterSpacing: '-0.01em',
              lineHeight: 1.4,
              ml:1
            }}
          >
            Risk Sources & Root Cause Analysis — {sectionLabel}
          </Typography>
                    {/* <Button
              size="small"
              onClick={handleSectionNASwitch}
              sx={{
                ml:2,
                px:0,
                minWidth:'auto',
                fontSize:'0.75rem',
                textTransform:'none',
                color: isSectionNA ? '#DC2626' : '#6B7280',
                '&:hover': { background:'transparent', color:'#111827' }
              }}
            >
              {isSectionNA ? 'Re-apply' : 'Skip'}
            </Button> */}
          
           {isLoading && (
            <Box sx={{ 
              position: 'relative', 
              display: 'inline-flex', 
              ml: 2,
            }}>
              <CircularProgress 
                size={20} 
                thickness={4}
                sx={{ color: '#3B82F6' }}
              />
              <SmartToyOutlinedIcon
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: 12,
                  color: '#3B82F6',
                }}
              />
            </Box>
          )}

            
        </Box>
      </Box>

      {/* Enhanced Table Section */}
      <Box 
        sx={{ 
          backgroundColor: '#F9FAFB', 
          borderRadius: 2, 
          p: 0,
          border: '1px solid #E5E7EB',
        }}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: 2,
            overflow: 'hidden',
            border: 'none',
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
              {renderTableHead()}
            </TableHead>
            <TableBody>
              {renderTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Root Cause Analysis Drawer */}
      {selectedRisk && (
        <RootCauseAnalysis
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          modelId={modelId}
          assessmentUuid={assessmentUuid}
          sectionName={sectionName}
          riskSourceName={selectedRisk.riskSource.name}
          toTitleCase={toTitleCase}
          evalTemplate={evalTemplate}
        />
      )}
    </Box>
  );
};

export default RiskCard;